/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ReportsMetricTile } from './ReportsMetricTile';
import { ReportsPaymentMixDonut } from './ReportsPaymentMixDonut';
import { ReportsErrorRetryCard } from './ReportsErrorRetryCard';
import { formatMoney } from '../../../shared/format/public-api';
import type { BranchTreasuryPeriodSummary } from '../../domain/reportsTreasuryTypes';
import {
  TREASURY_DISCLAIMER_LINES,
  treasuryCashOutflowCategoryLabel,
  treasuryCollectionMethodLabel,
  treasurySettlementMethodLabel,
} from '../reportsTreasuryFormat';

type Props = {
  variant: 'compact' | 'detail';
  summary: BranchTreasuryPeriodSummary | null;
  loading: boolean;
  error: string | null;
  periodHint: string;
  paymentMethodNames?: Record<string, string>;
  layoutWide?: boolean;
  onRetry?: () => void;
  onOpenDetail?: () => void;
  stale?: boolean;
};

export function ReportsTreasuryPeriodSection({
  variant,
  summary,
  loading,
  error,
  periodHint,
  paymentMethodNames,
  layoutWide = false,
  onRetry,
  onOpenDetail,
  stale,
}: Props) {
  const [helpOpen, setHelpOpen] = useState(false);
  const tileLayout = layoutWide ? 'wide' : 'default';
  const showDetailBlocks = variant === 'detail';

  if (error && !summary) {
    return (
      <ReportsErrorRetryCard
        message={error}
        showRetry={Boolean(onRetry)}
        onRetry={() => onRetry?.()}
        retryAccessibilityLabel="Reintentar cargar plata del período"
      />
    );
  }

  if (!summary && loading) {
    return (
      <PaperSheet padded style={styles.kpiSheet}>
        <Text style={styles.kpiSheetTitle}>Plata del período</Text>
        <Text style={[styles.caption, { marginTop: spacing.xs }]}>Cargando…</Text>
      </PaperSheet>
    );
  }

  if (!summary) {
    return null;
  }

  const balanceTone =
    summary.treasuryBalance >= 0 ? ('positive' as const) : ('negative' as const);

  const paymentMixItems = summary.collectionsByMethod.map((row) => ({
    methodId: row.methodId,
    total: row.total,
  }));

  return (
    <>
      <PaperSheet
        padded
        style={[styles.kpiSheet, stale && styles.ventasStaleData]}
        testID="informes.tesoreria.sheet"
      >
        <Text style={styles.kpiSheetTitle}>Plata del período</Text>
        <Text style={styles.kpiSheetHint}>
          {periodHint}
          {loading ? ' · actualizando…' : ''}
        </Text>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            layout={tileLayout}
            label="Cobraste"
            value={formatMoney(summary.collectionsTotal)}
            valueTone="emphasis"
            sub={`Ventas ${formatMoney(summary.salesCollectionsTotal)}${
              summary.customerPaymentsTotal > 0
                ? ` · Clientes ${formatMoney(summary.customerPaymentsTotal)}`
                : ''
            }`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Pagaste y retiraste"
            value={formatMoney(summary.outflowsTotal)}
            valueTone={summary.outflowsTotal > 0 ? 'warning' : 'default'}
            sub={
              summary.ownerWithdrawalsTotal > 0
                ? `Retiros del dueño ${formatMoney(summary.ownerWithdrawalsTotal)}`
                : 'Liquidaciones y egresos de caja'
            }
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Te debería quedar"
            value={formatMoney(summary.treasuryBalance)}
            valueTone={balanceTone}
            sub="Cobraste menos lo que salió"
          />
        </View>

        <View style={treasuryStyles.disclaimerRow}>
          <Text style={styles.caption}>
            No es margen de productos ni saldo bancario.{' '}
            <Text
              style={treasuryStyles.link}
              onPress={() => setHelpOpen(true)}
              accessibilityRole="link"
            >
              ¿Cómo se calcula?
            </Text>
          </Text>
        </View>

        {variant === 'compact' && onOpenDetail ? (
          <TouchableOpacity
            style={[styles.secondaryButton, { marginTop: spacing.sm, alignSelf: 'flex-start' }]}
            onPress={onOpenDetail}
            accessibilityRole="button"
            accessibilityLabel="Ver detalle de tesorería en Operación"
          >
            <Text style={styles.secondaryButtonText}>Ver detalle</Text>
          </TouchableOpacity>
        ) : null}
      </PaperSheet>

      {showDetailBlocks ? (
        <>
          <PaperSheet padded style={[styles.kpiSheet, stale && styles.ventasStaleData]}>
            <Text style={styles.reportsDenseBlockTitle}>Entradas por medio</Text>
            {paymentMixItems.length > 0 ? (
              <ReportsPaymentMixDonut
                items={paymentMixItems}
                methodNames={{
                  ...paymentMethodNames,
                  customer_payment: 'Cobros a clientes',
                  cash_inflow_manual: 'Ingreso manual en caja',
                }}
                total={summary.collectionsTotal}
              />
            ) : (
              <Text style={styles.caption}>No hubo cobros en este período.</Text>
            )}
          </PaperSheet>

          {summary.expenseSettlementsByMethod.length > 0 ? (
            <PaperSheet padded>
              <Text style={styles.reportsDenseBlockTitle}>Pagos de gastos</Text>
              {summary.expenseSettlementsByMethod.map((row, idx) => (
                <View
                  key={row.method}
                  style={[
                    styles.reportsDenseListFieldRow,
                    idx > 0 && treasuryStyles.rowBorder,
                  ]}
                >
                  <Text style={styles.reportsDenseListFieldLabel}>
                    {treasurySettlementMethodLabel(row.method)}
                  </Text>
                  <Text style={styles.reportsDenseListFieldValue}>{formatMoney(row.total)}</Text>
                </View>
              ))}
            </PaperSheet>
          ) : null}

          {summary.outflowsByCashCategory.length > 0 ? (
            <PaperSheet padded>
              <Text style={styles.reportsDenseBlockTitle}>Egresos de caja (sin liquidación)</Text>
              {summary.outflowsByCashCategory.map((row, idx) => (
                <View
                  key={row.category}
                  style={[
                    styles.reportsDenseListFieldRow,
                    idx > 0 && treasuryStyles.rowBorder,
                  ]}
                >
                  <Text style={styles.reportsDenseListFieldLabel}>
                    {treasuryCashOutflowCategoryLabel(row.category)}
                  </Text>
                  <Text style={styles.reportsDenseListFieldValue}>{formatMoney(row.total)}</Text>
                </View>
              ))}
            </PaperSheet>
          ) : null}
        </>
      ) : null}

      <Modal visible={helpOpen} transparent animationType="fade" onRequestClose={() => setHelpOpen(false)}>
        <Pressable style={treasuryStyles.modalBackdrop} onPress={() => setHelpOpen(false)}>
          <Pressable style={treasuryStyles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={treasuryStyles.modalTitle}>Cómo se calcula</Text>
            {TREASURY_DISCLAIMER_LINES.map((line) => (
              <Text key={line} style={treasuryStyles.modalLine}>
                {line}
              </Text>
            ))}
            {summary.doubleCountPolicyNote ? (
              <Text style={[treasuryStyles.modalLine, { marginTop: spacing.sm }]}>
                {summary.doubleCountPolicyNote}
              </Text>
            ) : null}
            <TouchableOpacity
              style={[styles.primaryButton, { marginTop: spacing.md }]}
              onPress={() => setHelpOpen(false)}
            >
              <Text style={styles.primaryButtonText}>Entendido</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const treasuryStyles = StyleSheet.create({
  disclaimerRow: { marginTop: spacing.sm },
  link: { color: ticketColors.gold, fontWeight: '700' },
  rowBorder: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    paddingTop: spacing.xs,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  modalCard: {
    backgroundColor: ticketColors.card,
    borderRadius: 12,
    padding: spacing.lg,
    maxWidth: 420,
    alignSelf: 'center',
    width: '100%',
  },
  modalTitle: { fontSize: 17, fontWeight: '800', color: ticketColors.ink, marginBottom: spacing.sm },
  modalLine: { fontSize: 14, color: ticketColors.ink2, lineHeight: 20, marginBottom: spacing.xs },
});
